import { rewriteResourceURL, getLatestRcsResources } from '@readcloud/common';
import {
  AppState,
  Book,
  Cloud,
  ReaderState,
  UserState,
  ViewState,
} from '@readcloud/data';

import { getPrintConfigFromRcsData } from '../utils/documentUtils';

export const useReaderState = (
  book: Book,
  cloud: Cloud,
  appStates: AppState[],
  user: UserState,
  pageNumber?: number,
  initialLocation?: string
): ReaderState => {
  let viewState: ViewState; //Define ViewState
  let version: string;
  if (!book || !cloud) {
    return {
      reader: undefined,
      user: undefined,
    };
  }

  // Determine initial location - prefer initialLocation (EPUB CFI), then pageNumber
  const getInitialLocation = () => {
    if (initialLocation) return initialLocation;
    if (pageNumber) return pageNumber.toString();
    return '';
  };

  const getInitialPageNumber = () => {
    if (pageNumber) return pageNumber;
    // For EPUB CFI, we don't have a page number
    if (initialLocation) return 0;
    return 0;
  };

  if (appStates) {
    let foundViewState = false; //technically there should only be 1 viewstate, but it seemed to be finding multiple for some books. Added this to stop at the first one.
    appStates.forEach((appState) => {
      const appViewState = appState.state as ViewState;
      if (
        appState.tags &&
        appState.tags.bookId === book.id &&
        appState.tags.realm === 'ViewState' &&
        appViewState
      ) {
        if (!foundViewState) {
          version = appState.version;
          viewState = {
            location: getInitialLocation() ?? appViewState.location ?? '',
            pageNumberEnd: getInitialPageNumber() ?? appViewState.pageNumberEnd ?? 0,
            pageNumberStart: getInitialPageNumber() ?? appViewState.pageNumberStart ?? 0,
            ...appViewState,
            // Override with initial values if provided
            ...(initialLocation || pageNumber ? {
              location: getInitialLocation(),
              pageNumberEnd: getInitialPageNumber(),
              pageNumberStart: getInitialPageNumber(),
            } : {}),
          };
        }
        foundViewState = true;
      }
    });
  } else {
    const location = getInitialLocation() ?? '1';
    const pageNum = getInitialPageNumber() ?? 1;
    viewState = {
      location,
      pageNumberEnd: pageNum,
      pageNumberStart: pageNum,
    };
  }

  const latest = book?.rcsData
    ? getLatestRcsResources(book.rcsData)
    : undefined;

  const fileUrl =
    (latest && latest.bookbytesUrl) ||
    (book.downloadURI && rewriteResourceURL(book.downloadURI));

  const printRights = getPrintConfigFromRcsData(book?.rcsData);

  //#################################################################

  return {
    reader: {
      bookId: book.id,
      cloudId: cloud.id,
      fileUrl,
      isbn: book.isbn || undefined,
      longId: latest && latest.bookbytes ? latest.bookbytes.longId : undefined,
      minor: latest && latest.bookbytes ? latest.bookbytes.minor : undefined,
      title: book.title,
      rcsData: book.rcsData || undefined,
      viewState: viewState,
      version: version,
      bookOptions: book.options,
      printRights: printRights,
      locationIndexStart: 0,
      locationIndexEnd: 0,
      loadingPageLabels: false,
      numPages: 0,
      lockScroll: false,
      outline: [],
      printMode: false,
    },
    user,
  };
};
